import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Spinner from '@/components/ui/Spinner';
import { getErrorMessage } from '@/utils/errors';
import {
  getTokenConsumption,
  getCreationUsage,
  getEvalActivity,
  getCreditPool,
} from '@/services/creditStatsApi';
import type {
  TokenConsumptionResponse,
  TemplateCreationUsageResponse,
  EvalActivityResponse,
  CreditPoolResponse,
  StatsPeriod,
  StatsGroupBy,
} from '@/types/creditStats';

// ─── Panel state helpers ─────────────────────────────────────────────────────

interface PanelState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function initPanel<T>(): PanelState<T> {
  return { data: null, loading: true, error: null };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function PanelError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        padding: '20px',
        borderRadius: '10px',
        backgroundColor: 'rgba(240,93,94,0.08)',
        border: '1px solid rgba(240,93,94,0.25)',
        color: 'var(--color-danger)',
        fontSize: '13px',
        textAlign: 'center',
      }}
    >
      {message}
    </div>
  );
}

function PanelEmpty({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '32px 20px',
        textAlign: 'center',
        color: 'var(--color-text-muted)',
        fontSize: '14px',
      }}
    >
      {message}
    </div>
  );
}

function PanelLoading() {
  return (
    <div style={{ padding: '32px 20px', display: 'flex', justifyContent: 'center' }}>
      <Spinner size="md" label="Loading..." />
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
        padding: '12px 16px',
        borderRadius: '10px',
        backgroundColor: 'var(--color-dark-elevated-2)',
        border: '1px solid var(--color-border-subtle)',
        minWidth: '0',
      }}
    >
      <span
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: '22px',
          fontWeight: 700,
          color: accent ?? 'var(--color-text-primary)',
          fontFamily: 'var(--font-heading)',
          letterSpacing: '-0.5px',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h2
      style={{
        fontFamily: 'var(--font-heading)',
        fontWeight: 600,
        fontSize: '15px',
        color: 'var(--color-text-primary)',
        margin: '0 0 16px',
        letterSpacing: '-0.2px',
      }}
    >
      {title}
    </h2>
  );
}

// ─── Owners-only state ───────────────────────────────────────────────────────

function OwnersOnly({ slug }: { slug: string }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid="owners-only-state"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: '16px',
        textAlign: 'center',
        padding: '32px',
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          backgroundColor: 'rgba(61,169,252,0.1)',
          border: '1px solid rgba(61,169,252,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          style={{ width: '24px', height: '24px', color: 'var(--color-accent-blue)' }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
          />
        </svg>
      </div>
      <div>
        <p
          style={{
            fontSize: '15px',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            margin: '0 0 6px',
          }}
        >
          Channel owners only
        </p>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: 0, maxWidth: '300px' }}>
          Stats are only visible to channel owners and admins.
        </p>
      </div>
      <button
        onClick={() => navigate(`/channels/${slug}`)}
        style={{
          padding: '8px 20px',
          borderRadius: '999px',
          border: '1px solid var(--color-border-subtle)',
          background: 'none',
          color: 'var(--color-text-secondary)',
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'border-color 150ms ease, color 150ms ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-accent-blue)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-accent-blue)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-subtle)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-secondary)';
        }}
      >
        Back to channel
      </button>
    </div>
  );
}

// ─── Token Consumption Panel ─────────────────────────────────────────────────

function TokenConsumptionPanel({
  state,
  poolState,
  groupBy,
  onGroupByChange,
}: {
  state: PanelState<TokenConsumptionResponse>;
  poolState: PanelState<CreditPoolResponse>;
  groupBy: StatsGroupBy;
  onGroupByChange: (g: StatsGroupBy) => void;
}) {
  const data = state.data;
  const pool = poolState.data;

  const topItems = data ? [...data.breakdown]
    .sort((a, b) => b.credits_consumed - a.credits_consumed)
    .slice(0, 10) : [];

  const chartData = topItems.map((item) => ({
    name: item.display_name ?? item.user_id.slice(0, 8),
    credits: item.credits_consumed,
    evals: item.evals_count,
  }));

  return (
    <div
      data-testid="token-consumption-panel"
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '16px',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <SectionHeader title="Token Consumption" />

        {/* Group-by toggle */}
        <div
          style={{
            display: 'inline-flex',
            borderRadius: '8px',
            border: '1px solid var(--color-border-subtle)',
            overflow: 'hidden',
            backgroundColor: 'var(--color-dark-elevated-2)',
          }}
        >
          {(['coach', 'athlete'] as StatsGroupBy[]).map((g) => (
            <button
              key={g}
              data-testid={`groupby-${g}`}
              onClick={() => onGroupByChange(g)}
              style={{
                padding: '5px 14px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor:
                  groupBy === g ? 'rgba(61,169,252,0.14)' : 'transparent',
                color:
                  groupBy === g ? 'var(--color-accent-blue)' : 'var(--color-text-muted)',
                transition: 'background-color 120ms ease, color 120ms ease',
                textTransform: 'capitalize',
              }}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {/* Summary stats row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '10px',
          marginBottom: '20px',
        }}
      >
        {state.loading ? (
          <div style={{ gridColumn: '1 / -1' }}>
            <PanelLoading />
          </div>
        ) : state.error ? null : data ? (
          <>
            <StatCard
              label="Total consumed"
              value={data.total_consumed.toLocaleString()}
              accent="var(--color-accent-blue)"
            />
            {pool && !poolState.loading && !poolState.error && (
              <>
                <StatCard
                  label="Available"
                  value={pool.available_credits.toLocaleString()}
                  accent="var(--color-accent)"
                />
                <StatCard
                  label="Reserved"
                  value={pool.reserved_credits.toLocaleString()}
                />
              </>
            )}
          </>
        ) : null}
      </div>

      {/* Chart */}
      {state.loading ? null : state.error ? (
        <PanelError message={state.error} />
      ) : chartData.length === 0 ? (
        <PanelEmpty message="No consumption data for this period." />
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--color-border-subtle)"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={-20}
              textAnchor="end"
              height={44}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-dark-elevated-2)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '8px',
                fontSize: '12px',
                color: 'var(--color-text-primary)',
              }}
              cursor={{ fill: 'rgba(61,169,252,0.06)' }}
              formatter={(value) => [(value as number).toLocaleString(), 'Credits']}
            />
            <Bar dataKey="credits" radius={[4, 4, 0, 0]} maxBarSize={40}>
              {chartData.map((_entry, index) => (
                <Cell
                  key={index}
                  fill={`rgba(61,169,252,${0.85 - index * 0.06})`}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Template Creation Panel ─────────────────────────────────────────────────

function TemplateCreationPanel({
  state,
}: {
  state: PanelState<TemplateCreationUsageResponse>;
}) {
  const coaches = state.data?.coaches ?? [];

  const chartData = coaches.map((c) => ({
    name: c.display_name ?? c.user_id.slice(0, 8),
    created: c.templates_this_month,
    limit: c.limit,
    atLimit: c.limit !== null && c.templates_this_month >= c.limit,
    nearLimit: c.limit !== null && c.templates_this_month >= c.limit * 0.9,
  }));

  return (
    <div
      data-testid="template-creation-panel"
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <SectionHeader title="Template Creation (this month)" />

      {state.loading ? (
        <PanelLoading />
      ) : state.error ? (
        <PanelError message={state.error} />
      ) : chartData.length === 0 ? (
        <PanelEmpty message="No coach data available." />
      ) : (
        <>
          {/* Legend */}
          <div
            style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '14px',
              flexWrap: 'wrap',
            }}
          >
            {[
              { color: 'rgba(30,215,96,0.8)', label: 'Under limit' },
              { color: 'rgba(244,201,93,0.85)', label: 'Near limit (90%+)' },
              { color: 'rgba(240,93,94,0.85)', label: 'At / over limit' },
            ].map(({ color, label }) => (
              <div
                key={label}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '2px',
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Visually-hidden status list — accessible to screen readers and tests */}
          <ul
            aria-label="coach usage status"
            data-testid="coach-status-list"
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
              clip: 'rect(0,0,0,0)',
              whiteSpace: 'nowrap',
              padding: 0,
              margin: 0,
              border: 0,
              listStyle: 'none',
            }}
          >
            {chartData.map((entry, i) => (
              <li
                key={i}
                data-testid={`coach-status-${i}`}
                data-near-limit={String(entry.nearLimit)}
                data-at-limit={String(entry.atLimit)}
              >
                {entry.name}: {entry.created} of {entry.limit ?? 'unlimited'}
                {entry.atLimit ? ' — at limit' : entry.nearLimit ? ' — near limit' : ' — under limit'}
              </li>
            ))}
          </ul>

          <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 40)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 0, right: 40, bottom: 0, left: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-border-subtle)"
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                axisLine={false}
                tickLine={false}
                width={100}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-dark-elevated-2)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--color-text-primary)',
                }}
                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                formatter={(value, _name, props) => {
                  const p = props as { payload?: { limit: number | null } };
                  const limit = p.payload?.limit;
                  const limitStr = limit === null ? 'unlimited' : String(limit);
                  return [`${value as number} / ${limitStr}`, 'Templates'];
                }}
              />
              <Bar dataKey="created" radius={[0, 4, 4, 0]} maxBarSize={24}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={
                      entry.atLimit
                        ? 'rgba(240,93,94,0.85)'
                        : entry.nearLimit
                        ? 'rgba(244,201,93,0.85)'
                        : 'rgba(30,215,96,0.8)'
                    }
                  />
                ))}
              </Bar>
              {/* Reference lines for limits */}
              {chartData.map((entry) =>
                entry.limit !== null ? (
                  <ReferenceLine
                    key={entry.name}
                    x={entry.limit}
                    stroke="rgba(255,255,255,0.15)"
                    strokeDasharray="4 3"
                  />
                ) : null,
              )}
            </BarChart>
          </ResponsiveContainer>

          {/* Unlimited coaches note */}
          {coaches.some((c) => c.limit === null) && (
            <p
              style={{
                marginTop: '10px',
                fontSize: '12px',
                color: 'var(--color-text-muted)',
              }}
            >
              Coaches without a dashed limit line have an unlimited quota.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Eval Activity Panel ─────────────────────────────────────────────────────

function EvalActivityPanel({
  state,
}: {
  state: PanelState<EvalActivityResponse>;
}) {
  const buckets = state.data?.buckets ?? [];

  const chartData = buckets.map((b) => ({
    week: b.week_start.slice(5), // "MM-DD"
    evals: b.evals_count,
  }));

  return (
    <div
      data-testid="eval-activity-panel"
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <SectionHeader title="Eval Activity (weekly)" />

      {state.loading ? (
        <PanelLoading />
      ) : state.error ? (
        <PanelError message={state.error} />
      ) : chartData.length === 0 ? (
        <PanelEmpty message="No evaluation activity for this period." />
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
            <defs>
              <linearGradient id="evalGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3DA9FC" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#3DA9FC" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--color-border-subtle)"
              vertical={false}
            />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-dark-elevated-2)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '8px',
                fontSize: '12px',
                color: 'var(--color-text-primary)',
              }}
              cursor={{ stroke: 'rgba(61,169,252,0.3)', strokeWidth: 1 }}
              formatter={(value) => [value as number, 'Evals']}
              labelFormatter={(label) => `Week of ${String(label)}`}
            />
            <Area
              type="monotone"
              dataKey="evals"
              stroke="#3DA9FC"
              strokeWidth={2}
              fill="url(#evalGrad)"
              dot={false}
              activeDot={{ r: 4, fill: '#3DA9FC', strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function ChannelStatsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [period, setPeriod] = useState<StatsPeriod>('30d');
  const [groupBy, setGroupBy] = useState<StatsGroupBy>('coach');
  const [forbidden, setForbidden] = useState(false);

  const [tokenState, setTokenState] =
    useState<PanelState<TokenConsumptionResponse>>(initPanel);
  const [creationState, setCreationState] =
    useState<PanelState<TemplateCreationUsageResponse>>(initPanel);
  const [evalState, setEvalState] =
    useState<PanelState<EvalActivityResponse>>(initPanel);
  const [poolState, setPoolState] =
    useState<PanelState<CreditPoolResponse>>(initPanel);

  // Detect 403 from any fetch and switch to owners-only view
  function check403(err: unknown): boolean {
    if (isAxiosError(err) && err.response?.status === 403) {
      setForbidden(true);
      return true;
    }
    return false;
  }

  // Fetch creation-usage and credit-pool once on mount (not period-sensitive)
  const fetchStaticPanels = useCallback(() => {
    if (!slug) return;

    setCreationState(initPanel());
    getCreationUsage(slug)
      .then((data) => setCreationState({ data, loading: false, error: null }))
      .catch((err) => {
        if (check403(err)) return;
        setCreationState({ data: null, loading: false, error: getErrorMessage(err) });
      });

    setPoolState(initPanel());
    getCreditPool(slug)
      .then((data) => setPoolState({ data, loading: false, error: null }))
      .catch((err) => {
        if (check403(err)) return;
        setPoolState({ data: null, loading: false, error: getErrorMessage(err) });
      });
  }, [slug]);

  // Fetch period-sensitive panels (token-consumption + eval-activity)
  const fetchPeriodPanels = useCallback(
    (p: StatsPeriod, g: StatsGroupBy) => {
      if (!slug) return;

      setTokenState(initPanel());
      getTokenConsumption(slug, p, g)
        .then((data) => setTokenState({ data, loading: false, error: null }))
        .catch((err) => {
          if (check403(err)) return;
          setTokenState({ data: null, loading: false, error: getErrorMessage(err) });
        });

      setEvalState(initPanel());
      getEvalActivity(slug, p)
        .then((data) => setEvalState({ data, loading: false, error: null }))
        .catch((err) => {
          if (check403(err)) return;
          setEvalState({ data: null, loading: false, error: getErrorMessage(err) });
        });
    },
    [slug],
  );

  // Initial load
  useEffect(() => {
    fetchStaticPanels();
    fetchPeriodPanels(period, groupBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Refetch period-dependent panels when period or groupBy changes (not on mount — already fired above)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchPeriodPanels(period, groupBy);
  }, [period, groupBy, fetchPeriodPanels]);

  if (forbidden) {
    return <OwnersOnly slug={slug ?? ''} />;
  }

  const periods: { value: StatsPeriod; label: string }[] = [
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: 'all', label: 'All time' },
  ];

  return (
    <>
      <style>{`
        @keyframes cs-fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cs-page { animation: cs-fade-in 0.38s cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>

      <div
        className="cs-page"
        style={{ maxWidth: '900px', margin: '0 auto', paddingBottom: '48px' }}
      >
        {/* Back nav */}
        <button
          onClick={() => navigate(`/channels/${slug}/manage`)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--color-text-muted)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            marginBottom: '16px',
            padding: 0,
            transition: 'color 150ms ease',
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-primary)')
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)')
          }
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Back to channel studio
        </button>

        {/* Page header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '16px',
            marginBottom: '24px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '22px',
                color: 'var(--color-text-primary)',
                margin: '0 0 4px',
                letterSpacing: '-0.3px',
              }}
            >
              Channel Stats
            </h1>
            {slug && (
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: 0 }}>
                {slug}
              </p>
            )}
          </div>

          {/* Period selector */}
          <div
            data-testid="period-selector"
            style={{
              display: 'inline-flex',
              borderRadius: '10px',
              border: '1px solid var(--color-border-subtle)',
              overflow: 'hidden',
              backgroundColor: 'var(--color-dark-elevated-2)',
            }}
          >
            {periods.map(({ value, label }) => (
              <button
                key={value}
                data-testid={`period-${value}`}
                onClick={() => setPeriod(value)}
                style={{
                  padding: '7px 16px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  backgroundColor:
                    period === value ? 'rgba(61,169,252,0.14)' : 'transparent',
                  color:
                    period === value ? 'var(--color-accent-blue)' : 'var(--color-text-muted)',
                  transition: 'background-color 120ms ease, color 120ms ease',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Panels grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <TokenConsumptionPanel
            state={tokenState}
            poolState={poolState}
            groupBy={groupBy}
            onGroupByChange={(g) => setGroupBy(g)}
          />
          <TemplateCreationPanel state={creationState} />
          <EvalActivityPanel state={evalState} />
        </div>
      </div>
    </>
  );
}

